import { type CarrierType } from '@/types/insurance.type.ts';

//
// Product Category - groupings of products
//
export const CATEGORY_TYPE = {
  // These need to be kept in sync with the backend
  // Different categories of products are displayed differently
  MOBILITY: 'Mobility',
  INCONTINENCE: 'Incontinence',
  DIABETES: 'Diabetes',
  ACCESSIBILITY: 'Home Accessibility',
  NUTRITION: 'Nutrition',
  GENERAL_HEALTH: 'General Health',
} as const;

export type CategoryType = (typeof CATEGORY_TYPE)[keyof typeof CATEGORY_TYPE];

export const HumanizedCategories: Record<CategoryType, string> = {
  [CATEGORY_TYPE.MOBILITY]: 'mobility aids',
  [CATEGORY_TYPE.INCONTINENCE]: 'incontinence supplies',
  [CATEGORY_TYPE.DIABETES]: 'diabetes supplies',
  [CATEGORY_TYPE.ACCESSIBILITY]: 'accessibility aids',
  [CATEGORY_TYPE.NUTRITION]: 'nutrition supplies',
  [CATEGORY_TYPE.GENERAL_HEALTH]: 'general health supplies',
};

export type ProductCategory = {
  uuid: string;
  parentUuid: string | null;
  label: string;
  description: string | null;
  imageUrl: string | null;
};

export function groupById(categories: ProductCategory[]): Map<string, ProductCategory> {
  return categories.reduce((acc, category) => {
    acc.set(category.uuid, category);
    return acc;
  }, new Map<string, ProductCategory>());
}

export type Product = {
  uuid: string;
  name: string;
  description: string | null;
  image: string;
  category: {
    uuid: string;
    name: string;
  };
  eligibility: {
    type: CarrierType | undefined;
    label: string;
    templateUuid: string;
  }[];
};

export function groupByCategory(products: Product[]): Map<string, Product[]> {
  return products.reduce((acc, product) => {
    const categoryId = product.category.uuid;
    if (!acc.has(categoryId)) acc.set(categoryId, []);
    acc.get(categoryId)!.push(product);
    return acc;
  }, new Map<string, Product[]>());
}

export type PageRequirements = {
  categoryType: CategoryType;
  patientCarrierTypes: Set<CarrierType>;
};

export function isProductInPage(product: Product, pageRequirements: PageRequirements): boolean {
  const { categoryType, patientCarrierTypes } = pageRequirements;
  return (
    product.category.name === categoryType && isInsuranceEligible(product, patientCarrierTypes)
  );
}

export function isInsuranceEligible(product: Product, carrierTypes: Set<CarrierType>): boolean {
  return product.eligibility.some((eligibility) =>
    carrierTypes.has(eligibility.type as CarrierType)
  );
}
